import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Constants } from '../../../../../shared/interfaces/constants';
import { MatOption } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { TagsDropAssignService } from '../../../../../shared/services/tags-drop-assign.service';
import { CdkDropList } from '@angular/cdk/drag-drop';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { Subject, takeUntil } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SharedService } from '../../../../../shared/services/shared.service';
import { CommonModule } from '@angular/common';
import { ManagementClientResponse } from 'api-client';

@Component({
	selector: 'app-transcriptions-filters',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatCardModule,
		MatDividerModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		MatCheckboxModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatSelectModule,
		CdkDropList,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './transcriptions-filters.component.html',
	styleUrl: './transcriptions-filters.component.scss',
})
export class TranscriptionsFiltersComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();
	@Input() role!: 'admin' | 'client';
	@Input() clientsList: any;
	@Input() resetFilters: any;
	@Input() apiLoading!: boolean;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	filtersFormGroup: FormGroup = new FormGroup({});
	transcriptionTypes = Constants.TRANSCRIPTION_TYPES;
	transcriptionLangs = Constants.TRANSCRIPTION_LANGS;
	filteredClients: ManagementClientResponse[] | undefined;
	@ViewChild('selectAllTranscriptionTypes') private selectAllTranscriptionTypes!: MatOption;

	DAYS_RANGE = 7;
	DATE_RANGE_LIMITS = {
		dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
		dateTo: new Date(),
	};

	filtersActive = false;

	// CLIPPING-592 - mirrors TranscriptionUtils.validateKeywordSearchFilter (backend). Keep the
	// two numbers in step with MIN_PREFIX_LETTERS_BEFORE_STAR / MAX_SEARCH_RANGE_DAYS there.
	private readonly MIN_PREFIX_LETTERS_BEFORE_STAR = 3;
	private readonly MAX_SEARCH_RANGE_DAYS = 31;

	constructor(
		private fb: FormBuilder,
		public tagsDropAssign: TagsDropAssignService,
		public utils: UtilsService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		this.formInitializer();
	}

	ngOnInit(): void {
		this.formControlWatchers();

		this.sharedService.requestActionInComponent.pipe(takeUntil(this.onDestroy)).subscribe({
			next: response => {
				if (response.componentName === 'TranscriptionsFiltersComponent') {
					switch (response.action) {
						case 'FILTER_TRIGGER': {
							this.filterListItems();
							break;
						}
					}
				}
			},
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.selectAllTranscriptionTypes.select();
			this.transcriptionTypeSelection();
		}, 1000);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['resetFilters'] && !changes['resetFilters'].currentValue) {
			this.filtersActive = false;
			this.resetForm();
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	private formInitializer() {
		this.filtersFormGroup = this.fb.group({
			clientId: [],
			types: [[]],
			langs: [this.transcriptionLangs.map(lang => lang.id)],
			text: [],
			speaker: [],
			tagIds: [],
			createdAtFrom: [this.DATE_RANGE_LIMITS.dateFrom],
			createdAtTo: [this.DATE_RANGE_LIMITS.dateTo],
		});
	}

	private formControlWatchers() {
		if (this.filtersFormGroup.controls['clientId']) {
			this.filtersFormGroup.controls['clientId'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.searchClients(searchTerm);
			});
		}
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.filtersFormGroup.reset();
		if (formDirective) {
			formDirective.resetForm();
		}

		this.filtersFormGroup.controls['types'].setValue([]);
		this.filtersFormGroup.controls['langs'].setValue(this.transcriptionLangs.map(lang => lang.id));
		if (this.selectAllTranscriptionTypes) {
			this.selectAllTranscriptionTypes.select();
			this.transcriptionTypeSelection();
		}

		this.filteredClients = this.clientsList;

		this.filtersFormGroup.controls['createdAtFrom'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
		this.filtersFormGroup.controls['createdAtTo'].setValue(this.DATE_RANGE_LIMITS.dateTo);

		if (this.role === 'admin') {
			this.sharedService.requestActionInComponent.next({
				componentName: 'TagsComponent',
				action: 'DESELECT_TAGS',
			});
		}

		this.filterListItems();
	}

	getFiltersFormData() {
		const formData = this.filtersFormGroup.value;

		let createdAtFrom = this.filtersFormGroup.controls['createdAtFrom'].value;
		if (createdAtFrom) {
			createdAtFrom = new Date((createdAtFrom as Date).setHours(0, 0, 0)).getTime();
		}

		let createdAtTo = this.filtersFormGroup.controls['createdAtTo'].value;
		if (createdAtTo) {
			createdAtTo = new Date((createdAtTo as Date).setHours(0, 0, 0));
			createdAtTo = new Date(new Date(createdAtTo).setDate(new Date(createdAtTo).getDate() + 1)).getTime();
		}

		formData.createdAtFrom = createdAtFrom;
		formData.createdAtTo = createdAtTo;

		return this.filtersFormGroup.value;
	}

	getDateLimitRange() {
		return this.filtersFormGroup.controls['createdAtTo'].value;
	}

	/** Select transcription type*/
	transcriptionTypeSelection(itemSelected?: Event) {
		if (itemSelected) {
			if (this.selectAllTranscriptionTypes.selected) {
				this.selectAllTranscriptionTypes.deselect();
			} else if (this.filtersFormGroup.controls['types'].value.length === this.transcriptionTypes.length) {
				this.selectAllTranscriptionTypes.select();
			}
		} else {
			this.filtersFormGroup.controls['types'].patchValue(this.selectAllTranscriptionTypes.selected ? ['ALL', ...this.transcriptionTypes.map(item => item.id)] : [], 0);
		}
	}

	/** Filter input client name */
	private searchClients(searchTerm: string) {
		if (searchTerm && searchTerm.length >= 1) {
			this.filteredClients = this.clientsList.filter((client: ManagementClientResponse) =>
				client.name?.toLowerCase().includes(searchTerm.toLowerCase()));
		} else {
			this.filteredClients = this.clientsList;
		}
	}
	displayClientFn(client: ManagementClientResponse): string {
		return client && client.name ? client.name : '';
	}

	/** FILTERING: construct DTO with all filter data*/
	filterListItems() {
		this.filtersActive = false;

		let createdAtFrom = this.filtersFormGroup.controls['createdAtFrom'].value;
		if (createdAtFrom) {
			createdAtFrom = new Date((createdAtFrom as Date).setHours(0, 0, 0)).getTime();
		}

		let createdAtTo = this.filtersFormGroup.controls['createdAtTo'].value;
		if (createdAtTo) {
			createdAtTo = new Date((createdAtTo as Date).setHours(0, 0, 0));
			createdAtTo = new Date(new Date(createdAtTo).setDate(new Date(createdAtTo).getDate() + 1)).getTime();
		}

		if (this.tagsDropAssign.manuallyselectedTags) {
			this.filtersFormGroup.controls['tagIds'].setValue(this.tagsDropAssign.manuallyselectedTags.map(tag => tag.id));
		} else {
			this.filtersFormGroup.controls['tagIds'].setValue([]);
		}

		const filterDto = JSON.parse(JSON.stringify(this.filtersFormGroup.value));
		filterDto.createdAtFrom = createdAtFrom;
		filterDto.createdAtTo = createdAtTo;

		if (this.filtersFormGroup.controls['types'].value) {
			filterDto.types = this.filtersFormGroup.controls['types'].value.find((item: string) => item === 'ALL') ? [] : this.filtersFormGroup.controls['types'].value;
		}

		filterDto.clientId = this.filtersFormGroup.controls['clientId'].value ? this.filtersFormGroup.controls['clientId'].value.id : null;

		const validationError = this.validateSearchFilter(filterDto.createdAtFrom, filterDto.createdAtTo, filterDto.text);
		if (validationError) {
			this.sharedService.showNotification.next({ message: validationError, type: 'ERROR' });
			return;
		}

		this.filtersActive = !Object.keys(filterDto).every((key: string) => filterDto[key] === null || (filterDto[key] && filterDto[key].length === 0));

		this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0 });
	}

	// CLIPPING-592. Same three rules as TranscriptionUtils.validateKeywordSearchFilter (backend) -
	// checked here first so the user sees a clear message immediately instead of waiting on a
	// round-trip for something that was always going to be rejected with a 400.
	private validateSearchFilter(createdAtFrom: number | null, createdAtTo: number | null, text: string | null): string | null {
		if (!createdAtFrom || !createdAtTo) {
			return this.translate.instant('TRANSCRIPTIONS.SEARCH_DATE_RANGE_REQUIRED');
		}

		const hasText = !!text && text.trim().length > 0;

		// The 31-day cap only applies once there is actual search text - a plain date-range
		// browse (no text) keeps allowing any range, same as before CLIPPING-592.
		if (hasText) {
			const rangeDays = (createdAtTo - createdAtFrom) / (1000 * 60 * 60 * 24);
			if (rangeDays > this.MAX_SEARCH_RANGE_DAYS) {
				return this.translate.instant('TRANSCRIPTIONS.SEARCH_DATE_RANGE_TOO_LONG');
			}

			for (const word of text!.trim().split(/\s+/)) {
				if (word.endsWith('*') && word.length - 1 < this.MIN_PREFIX_LETTERS_BEFORE_STAR) {
					return this.translate.instant('TRANSCRIPTIONS.SEARCH_PREFIX_TOO_SHORT', { word });
				}
			}
		}

		return null;
	}
}